import React, {Fragment, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useRecoilState} from 'recoil';
import {makeRequest} from '../../utils/httpRequest';
import Config from '../../core/config';
import {loadingAtom} from '../../atoms';
import {UserPermission, CompanyPermission} from '../../utils/utils';
import Loading from '../../core/Loading';
import {usePermission} from '../../core/PermissionContext';
import {getCurrentUserId} from '../../utils/auth';
import DeleteButton from '../common/DeleteButton';
import {showConfirm, showAlert} from '../../core/dialog';
import ShoppingListItemSelectAsync from './ShoppingListItemSelectAsync';
import './shoppingList.css';
import {
    getDefaultShoppingListName,
    getShoppingItemKey,
    isShoppingItemFilled,
    nextShoppingItemClientKey,
    sortShoppingListItems,
    SUPERMARKET_SECTORS,
} from './shoppingListUtils';

const apiUrl = Config.apiUrl;

const newItem = () => ({
    id: undefined,
    _clientKey: nextShoppingItemClientKey(),
    catalogItem: {id: undefined, name: ''},
    quantity: 1,
    observation: '',
    supermarketSector: '',
    purchased: false,
    sortOrder: 0,
});

const newListEntity = () => ({
    id: undefined,
    name: getDefaultShoppingListName(),
    sharedUserIds: [],
    defaultTemplate: false,
    ownerUserId: undefined,
});

const AUTO_SAVE_DELAY_MS = 600;
const PURCHASE_IDLE_MS = 1000;
const PURCHASED_ANIMATION_MS = 1000;

function parseQuantity(value) {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
}

function isShoppingItemCommitted(item) {
    return !!(item?.catalogItem?.id && String(item.catalogItem.id).trim() !== '');
}

function buildItemsPayload(itemsList) {
    const filled = (itemsList || []).filter((item) => isShoppingItemCommitted(item));
    const ordered = sortShoppingListItems(filled, {column: null, direction: 'asc'});
    return ordered.map((item, index) => ({
        id: item.id,
        itemName: item.catalogItem.name.trim(),
        quantity: String(parseQuantity(item.quantity)),
        observation: item.observation || '',
        supermarketSector: item.supermarketSector || '',
        purchased: !!item.purchased,
        sortOrder: index,
    }));
}

function mapServerItemsToState(serverItems) {
    const loadedItems = (serverItems || []).map((item, index) => ({
        id: item.id,
        _clientKey: item.id ? `db-${item.id}` : nextShoppingItemClientKey(),
        catalogItem: {id: item.itemName, name: item.itemName},
        quantity: parseQuantity(item.quantity),
        observation: item.observation || '',
        supermarketSector: item.supermarketSector || '',
        purchased: !!item.purchased,
        sortOrder: item.sortOrder ?? index,
    }));
    return loadedItems.length > 0 ? [...loadedItems, newItem()] : [newItem()];
}

const rebuildExpandedObsKeys = (list) => {
    const keys = new Set();
    (list || []).forEach((item) => {
        if (item.observation && item.observation.trim() !== '') {
            keys.add(getShoppingItemKey(item));
        }
    });
    return keys;
};

const SortableHeader = ({column, label, sortConfig, onSort, style, className = ''}) => {
    const active = sortConfig.column === column;
    const indicator = active ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : '';
    return (
        <th
            style={style}
            className={`shopping-list-sortable-th ${className}`.trim()}
            onClick={() => onSort(column)}
            title="Clique para ordenar"
        >
            {label}{indicator}
        </th>
    );
};

const ShoppingListManager = () => {
    const {hasUserPermission, hasCompanyPermission, loadingPermissions} = usePermission();
    const navigate = useNavigate();
    const [, setLoading] = useRecoilState(loadingAtom);
    const searchParams = new URLSearchParams(window.location.search);
    const id = searchParams.get('id');
    const currentUserId = getCurrentUserId();

    const [formData, setFormData] = useState(newListEntity());
    const [items, setItems] = useState([newItem()]);
    const [sortConfig, setSortConfig] = useState({column: null, direction: 'asc'});
    const [expandedObsKeys, setExpandedObsKeys] = useState(new Set());
    const [purchasePendingKeys, setPurchasePendingKeys] = useState(() => new Map());
    const [purchasingAnimKeys, setPurchasingAnimKeys] = useState(new Set());
    const [purchasingAnimModes, setPurchasingAnimModes] = useState(() => new Map());
    const [saveStatus, setSaveStatus] = useState('');
    const managerRef = useRef(null);
    const itemInputRefs = useRef({});
    const purchasePendingRef = useRef(new Map());
    const purchaseIdleTimerRef = useRef(null);
    const purchaseAnimTimersRef = useRef({});
    const itemsRef = useRef(items);
    const formDataRef = useRef(formData);
    const skipAutoSaveRef = useRef(true);
    const saveTimerRef = useRef(null);
    const saveInFlightRef = useRef(false);
    const pendingSaveRef = useRef(false);
    const suppressItemBlurCommitRef = useRef(false);

    useEffect(() => {
        itemsRef.current = items;
    }, [items]);

    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    const persistList = useCallback(async () => {
        const currentForm = formDataRef.current;
        const currentItems = itemsRef.current;
        const name = (currentForm.name || '').trim();
        if (!name) {
            return;
        }
        const payloadItems = buildItemsPayload(currentItems);
        if (payloadItems.length === 0) {
            return;
        }

        if (saveInFlightRef.current) {
            pendingSaveRef.current = true;
            return;
        }

        saveInFlightRef.current = true;
        setSaveStatus('Salvando...');

        try {
            const listId = currentForm.id;
            const isCreate = !listId;
            const payload = {
                id: listId,
                name,
                items: payloadItems,
            };
            if (isCreate) {
                payload.sharedUserIds = [];
            }

            const saved = await makeRequest(
                `${apiUrl}/api/shopping-lists`,
                isCreate ? 'POST' : 'PUT',
                {'Content-Type': 'application/json'},
                JSON.stringify(payload),
            );

            if (saved?.id) {
                skipAutoSaveRef.current = true;
                if (isCreate) {
                    navigate(`/app/shoppinglist?id=${saved.id}`, {replace: true});
                }
                setFormData((prev) => ({
                    ...prev,
                    id: saved.id,
                    name: saved.name,
                    sharedUserIds: saved.sharedUserIds || prev.sharedUserIds || [],
                    ownerUserId: saved.ownerUserId ?? prev.ownerUserId ?? currentUserId,
                    defaultTemplate: !!saved.defaultTemplate,
                }));
                const allRows = mapServerItemsToState(saved.items);
                setExpandedObsKeys(rebuildExpandedObsKeys(allRows));
                setItems(allRows);
                skipAutoSaveRef.current = false;
            }
            setSaveStatus('Salvo');
        } catch (error) {
            console.error('Error auto-saving shopping list:', error);
            setSaveStatus('Erro ao salvar');
        } finally {
            saveInFlightRef.current = false;
            if (pendingSaveRef.current) {
                pendingSaveRef.current = false;
                setTimeout(() => persistList(), 0);
            }
        }
    }, [navigate]);

    const scheduleAutoSave = useCallback(() => {
        if (skipAutoSaveRef.current) {
            return;
        }
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
        }
        saveTimerRef.current = setTimeout(() => {
            persistList();
        }, AUTO_SAVE_DELAY_MS);
    }, [persistList]);

    const hasPurchasePending = () => purchasePendingRef.current.size > 0;

    const clearPurchaseAnimTimer = (key) => {
        if (purchaseAnimTimersRef.current[key]) {
            clearTimeout(purchaseAnimTimersRef.current[key]);
            delete purchaseAnimTimersRef.current[key];
        }
    };

    const clearPurchaseIdleTimer = () => {
        if (purchaseIdleTimerRef.current) {
            clearTimeout(purchaseIdleTimerRef.current);
            purchaseIdleTimerRef.current = null;
        }
    };

    const startPurchaseTransitions = useCallback((transitions) => {
        transitions.forEach(({key, mode}) => {
            clearPurchaseAnimTimer(key);
            setPurchasingAnimKeys((prev) => new Set(prev).add(key));
            setPurchasingAnimModes((prev) => {
                const next = new Map(prev);
                next.set(key, mode);
                return next;
            });
            purchaseAnimTimersRef.current[key] = setTimeout(() => {
                clearPurchaseAnimTimer(key);
                setPurchasingAnimKeys((prev) => {
                    const next = new Set(prev);
                    next.delete(key);
                    return next;
                });
                setPurchasingAnimModes((prev) => {
                    const next = new Map(prev);
                    next.delete(key);
                    return next;
                });
                setItems((prevItems) => prevItems.map((row) => (
                    getShoppingItemKey(row) === key
                        ? {...row, purchased: mode === 'mark'}
                        : row
                )));
                scheduleAutoSave();
            }, PURCHASED_ANIMATION_MS);
        });
    }, [scheduleAutoSave]);

    const startPurchaseIdleCountdown = useCallback(() => {
        clearPurchaseIdleTimer();
        if (!hasPurchasePending()) {
            return;
        }
        purchaseIdleTimerRef.current = setTimeout(() => {
            purchaseIdleTimerRef.current = null;
            const transitions = [...purchasePendingRef.current.entries()].map(([key, mode]) => ({
                key,
                mode,
            }));
            purchasePendingRef.current = new Map();
            setPurchasePendingKeys(new Map());
            if (transitions.length > 0) {
                startPurchaseTransitions(transitions);
            }
        }, PURCHASE_IDLE_MS);
    }, [startPurchaseTransitions]);

    const cancelPurchaseTransition = useCallback((key) => {
        clearPurchaseAnimTimer(key);
        setPurchasingAnimKeys((prev) => {
            if (!prev.has(key)) {
                return prev;
            }
            const next = new Set(prev);
            next.delete(key);
            return next;
        });
        setPurchasingAnimModes((prev) => {
            if (!prev.has(key)) {
                return prev;
            }
            const next = new Map(prev);
            next.delete(key);
            return next;
        });
        if (!purchasePendingRef.current.has(key)) {
            return;
        }
        const nextPending = new Map(purchasePendingRef.current);
        nextPending.delete(key);
        purchasePendingRef.current = nextPending;
        setPurchasePendingKeys(nextPending);
        if (nextPending.size === 0) {
            clearPurchaseIdleTimer();
        } else {
            startPurchaseIdleCountdown();
        }
    }, [startPurchaseIdleCountdown]);

    useEffect(() => {
        const el = managerRef.current;
        if (!el) {
            return undefined;
        }
        const onInteraction = () => {
            if (hasPurchasePending()) {
                startPurchaseIdleCountdown();
            }
        };
        const events = ['pointerdown', 'keydown', 'input', 'change', 'touchstart'];
        events.forEach((eventName) => el.addEventListener(eventName, onInteraction, true));
        return () => {
            events.forEach((eventName) => el.removeEventListener(eventName, onInteraction, true));
        };
    }, [startPurchaseIdleCountdown]);

    useEffect(() => () => {
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
        }
        clearPurchaseIdleTimer();
        Object.keys(purchaseAnimTimersRef.current).forEach(clearPurchaseAnimTimer);
    }, []);

    const enableAutoSaveAndSchedule = useCallback(() => {
        skipAutoSaveRef.current = false;
        setTimeout(() => scheduleAutoSave(), 0);
    }, [scheduleAutoSave]);

    const displayItems = useMemo(
        () => sortShoppingListItems(items, sortConfig),
        [items, sortConfig],
    );

    const findItemIndexByKey = (key) =>
        items.findIndex((item) => getShoppingItemKey(item) === key);

    const updateItemByKey = (key, updater) => {
        const index = findItemIndexByKey(key);
        if (index < 0) return items;
        const updated = [...items];
        updated[index] = typeof updater === 'function' ? updater(updated[index]) : updater;
        return updated;
    };

    const canCreate = () =>
        hasUserPermission(UserPermission.CREATE_SHOPPING_LIST)
        && hasCompanyPermission(CompanyPermission.CREATE_SHOPPING_LIST);

    const canEdit = () =>
        hasUserPermission(UserPermission.EDIT_SHOPPING_LIST)
        && hasCompanyPermission(CompanyPermission.EDIT_SHOPPING_LIST);

    const getFilledItemNames = (excludeKey = '') =>
        items
            .filter((item) => {
                const key = getShoppingItemKey(item);
                return key !== excludeKey && item.catalogItem?.id;
            })
            .map((item) => item.catalogItem.name)
            .filter((name) => name && name.trim() !== '');

    const isDuplicateItem = (itemName, excludeKey = '') => {
        const normalized = (itemName || '').trim().toLowerCase();
        if (!normalized) return false;
        return items.some((item) =>
            getShoppingItemKey(item) !== excludeKey
            && item.catalogItem?.id
            && (item.catalogItem.name || '').trim().toLowerCase() === normalized
        );
    };

    const handleHeaderSort = (column) => {
        setSortConfig((prev) => ({
            column,
            direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const toggleObsRow = (key) => {
        setExpandedObsKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const togglePurchased = (key) => {
        const item = items.find((row) => getShoppingItemKey(row) === key);
        if (!item) {
            return;
        }

        if (purchasePendingKeys.has(key) || purchasingAnimKeys.has(key)) {
            cancelPurchaseTransition(key);
            return;
        }

        const mode = item.purchased ? 'unmark' : 'mark';
        const nextPending = new Map(purchasePendingRef.current);
        nextPending.set(key, mode);
        purchasePendingRef.current = nextPending;
        setPurchasePendingKeys(nextPending);
        setTimeout(() => startPurchaseIdleCountdown(), 0);
    };

    const applyTemplateItemsToTable = (sourceItems) => {
        const rows = (sourceItems || [])
            .filter((item) => item.itemName && item.itemName.trim() !== '')
            .map((item, index) => ({
                ...newItem(),
                catalogItem: {id: item.itemName, name: item.itemName},
                quantity: parseQuantity(item.quantity),
                observation: item.observation || '',
                supermarketSector: item.supermarketSector || '',
                purchased: false,
                sortOrder: index,
            }));
        const allRows = rows.length > 0 ? [...rows, newItem()] : [newItem()];
        setExpandedObsKeys(rebuildExpandedObsKeys(allRows));
        setItems(allRows);
    };

    async function loadDefaultItemsIntoTable() {
        try {
            const names = await makeRequest(`${apiUrl}/api/shopping-lists/default-items`, 'GET');
            const defaultRows = (names || []).map((name, index) => ({
                ...newItem(),
                catalogItem: {id: name, name},
                quantity: 1,
                sortOrder: index,
            }));
            const allRows = [...defaultRows, newItem()];
            setExpandedObsKeys(new Set());
            setItems(allRows);
        } catch (error) {
            console.error('Error loading default items:', error);
            setItems([newItem()]);
        }
    }

    async function loadDefaultTemplateIntoTable() {
        try {
            const template = await makeRequest(`${apiUrl}/api/shopping-lists/default-template`, 'GET');
            applyTemplateItemsToTable(template.items);
        } catch (error) {
            if (error?.statusCode !== 404) {
                console.error('Error loading default template list:', error);
            }
            await loadDefaultItemsIntoTable();
        }
    }

    const isListOwner = () => {
        const ownerId = formData.ownerUserId;
        if (currentUserId == null || ownerId == null) {
            return false;
        }
        return Number(ownerId) === Number(currentUserId);
    };

    async function toggleDefaultTemplate(checked) {
        const listId = formData.id;
        if (!listId || !isListOwner()) {
            return;
        }
        try {
            await makeRequest(
                `${apiUrl}/api/shopping-lists/${listId}/default-template?defaultTemplate=${checked}`,
                'PATCH',
            );
            setFormData((prev) => ({...prev, defaultTemplate: checked}));
        } catch (error) {
            console.error('Error updating default template flag:', error);
            showAlert('Erro ao atualizar a lista padrão.');
        }
    }

    async function loadList() {
        if (!id) {
            setFormData(newListEntity());
            await loadDefaultTemplateIntoTable();
            enableAutoSaveAndSchedule();
            return;
        }
        try {
            setLoading(true);
            const response = await makeRequest(`${apiUrl}/api/shopping-lists/id=${id}`, 'GET');
            setFormData({
                id: response.id,
                name: response.name,
                sharedUserIds: response.sharedUserIds || [],
                ownerUserId: response.ownerUserId,
                defaultTemplate: !!response.defaultTemplate,
            });
            const loadedItems = (response.items || []).map((item, index) => ({
                ...item,
                _clientKey: item.id ? `db-${item.id}` : nextShoppingItemClientKey(),
                catalogItem: {id: item.itemName, name: item.itemName},
                quantity: parseQuantity(item.quantity),
                supermarketSector: item.supermarketSector || '',
                purchased: !!item.purchased,
                sortOrder: item.sortOrder ?? index,
            }));
            const allRows = loadedItems.length > 0 ? [...loadedItems, newItem()] : [newItem()];
            setExpandedObsKeys(rebuildExpandedObsKeys(allRows));
            setItems(allRows);
            skipAutoSaveRef.current = false;
        } catch (error) {
            console.error('Error loading shopping list:', error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!loadingPermissions) {
            loadList();
        }
    }, [id, loadingPermissions]);

    const handleNameChange = (e) => {
        setFormData((prev) => ({...prev, name: e.target.value}));
        scheduleAutoSave();
    };

    const handleItemChange = (key, field, value) => {
        setItems(updateItemByKey(key, (item) => ({
            ...item,
            [field]: field === 'quantity' ? parseQuantity(value) : value,
        })));
        scheduleAutoSave();
    };

    const removeItem = async (key) => {
        if (!await showConfirm('Deseja realmente excluir este item?')) {
            return;
        }
        const index = findItemIndexByKey(key);
        const updatedItems = items.filter((_, i) => i !== index);
        const nextItems = updatedItems.length > 0 ? updatedItems : [newItem()];
        setExpandedObsKeys(rebuildExpandedObsKeys(nextItems));
        setItems(nextItems);
        scheduleAutoSave();
    };

    const selectItemName = (itemName, key) => {
        const name = (itemName || '').trim();
        if (!name) return;
        if (isDuplicateItem(name, key)) {
            showAlert('Este item já está na lista.');
            return;
        }
        const index = findItemIndexByKey(key);
        let updatedItems = updateItemByKey(key, (item) => ({
            ...item,
            catalogItem: {id: name, name},
            quantity: 1,
        }));
        if (index === items.length - 1) {
            updatedItems = [...updatedItems, newItem()];
        }
        setItems(updatedItems);
        scheduleAutoSave();
    };

    const handleItemInputChange = (inputValue, key) => {
        const index = findItemIndexByKey(key);
        const current = items[index];
        const hadSelectedId = !!current?.catalogItem?.id;

        let updatedItems = updateItemByKey(key, (item) => ({
            ...item,
            catalogItem: {id: undefined, name: inputValue},
            quantity: item.quantity || 1,
        }));

        if (!hadSelectedId && index === items.length - 1 && inputValue && inputValue.trim() !== '') {
            updatedItems = [...updatedItems, newItem()];
        }
        setItems(updatedItems);
    };

    const commitItemNameOnBlur = (key) => {
        if (suppressItemBlurCommitRef.current) {
            return;
        }

        const currentItems = itemsRef.current;
        const index = currentItems.findIndex((row) => getShoppingItemKey(row) === key);
        if (index < 0) {
            return;
        }

        const item = currentItems[index];
        const name = (item.catalogItem?.name || '').trim();

        if (!name) {
            if (item.catalogItem?.id) {
                const updated = [...currentItems];
                updated[index] = {...item, catalogItem: {id: undefined, name: ''}};
                setItems(updated);
                scheduleAutoSave();
            }
            return;
        }

        if (isDuplicateItem(name, key)) {
            showAlert('Este item já está na lista.');
            const updated = [...currentItems];
            updated[index] = {
                ...item,
                catalogItem: item.catalogItem?.id
                    ? {id: item.catalogItem.id, name: item.catalogItem.id}
                    : {id: undefined, name: ''},
            };
            setItems(updated);
            return;
        }

        const alreadyCommitted = item.catalogItem?.id === name;
        if (alreadyCommitted) {
            return;
        }

        let updated = [...currentItems];
        updated[index] = {
            ...item,
            catalogItem: {id: name, name},
        };
        if (index === updated.length - 1) {
            updated = [...updated, newItem()];
        }
        setItems(updated);
        scheduleAutoSave();
    };

    const focusItemInput = (key) => {
        setTimeout(() => {
            itemInputRefs.current[key]?.focus?.();
        }, 50);
    };

    const handleItemClick = (itemName, key) => {
        suppressItemBlurCommitRef.current = true;
        selectItemName(itemName, key);
        focusItemInput(key);
        setTimeout(() => {
            suppressItemBlurCommitRef.current = false;
        }, 200);
    };

    if (loadingPermissions) return <Loading />;

    const allowed = id ? canEdit() : canCreate();
    if (!allowed) {
        navigate('/unauthorizedaccess');
        return null;
    }

    return (
        <div ref={managerRef} className="app-form-page shopping-list-manager">
            <div className="shopping-list-manager-header">
                <div className="header-label">{id || formData.id ? 'Editar' : 'Nova'} Lista de Compras</div>
                {saveStatus && (
                    <span className="shopping-list-save-status">{saveStatus}</span>
                )}
            </div>

            <div className="shopping-list-field">
                <label>Nome da lista</label>
                <input
                    type="text"
                    value={formData.name}
                    onChange={handleNameChange}
                    placeholder="Ex.: Compras da semana"
                />
            </div>

            {(formData.id || id) && isListOwner() && (
                <label className="shopping-list-default-template-option">
                    <input
                        type="checkbox"
                        checked={!!formData.defaultTemplate}
                        onChange={(e) => toggleDefaultTemplate(e.target.checked)}
                        data-test="shopping-list-default-template"
                    />
                    Usar como lista padrão ao criar novas listas
                </label>
            )}

            <div className="shopping-list-section">
                {!id && !formData.id && (
                    <p className="shopping-list-hint">
                        Os itens da sua lista padrão (se houver) ou do catálogo inicial serão copiados (qtd. 1).
                        Remova o que não precisar e adicione novos na última linha.
                    </p>
                )}
                <table className="shopping-list-items-table">
                    <thead>
                        <tr>
                            <SortableHeader
                                column="purchased"
                                label=""
                                sortConfig={sortConfig}
                                onSort={handleHeaderSort}
                                style={{width: '36px'}}
                            />
                            <SortableHeader
                                column="item"
                                label="Item"
                                sortConfig={sortConfig}
                                onSort={handleHeaderSort}
                                style={{width: '30%', minWidth: '120px'}}
                            />
                            <SortableHeader
                                column="sector"
                                label="Setor"
                                sortConfig={sortConfig}
                                onSort={handleHeaderSort}
                                style={{width: '118px'}}
                            />
                            <SortableHeader
                                column="quantity"
                                label="Qtd."
                                sortConfig={sortConfig}
                                onSort={handleHeaderSort}
                                style={{width: '44px'}}
                            />
                            <th style={{width: '58px'}}/>
                        </tr>
                    </thead>
                    <tbody>
                        {displayItems.map((item) => {
                            const itemKey = getShoppingItemKey(item);
                            const filled = isShoppingItemFilled(item);
                            const obsExpanded = expandedObsKeys.has(itemKey);
                            const purchased = !!item.purchased;
                            const pendingMode = purchasePendingKeys.get(itemKey);
                            const isPendingMark = pendingMode === 'mark';
                            const isPendingUnmark = pendingMode === 'unmark';
                            const isPurchasingAnim = purchasingAnimKeys.has(itemKey);
                            const animMode = purchasingAnimModes.get(itemKey);
                            const isAnimMark = isPurchasingAnim && animMode === 'mark';
                            const showPurchasedStyle = purchased || isPurchasingAnim;
                            const checkboxChecked = (purchased && !isPendingUnmark) || isPendingMark || isAnimMark;
                            const rowClass = [
                                showPurchasedStyle ? 'shopping-list-item-row--purchased' : '',
                                isPurchasingAnim ? 'shopping-list-item-row--purchasing-fade' : '',
                            ].filter(Boolean).join(' ');
                            const namePurchasedClass = showPurchasedStyle
                                ? 'shopping-list-item-name--purchased'
                                : '';
                            return (
                                <Fragment key={itemKey}>
                                    <tr className={rowClass}>
                                        <td className="shopping-list-purchased-cell">
                                            {filled && (
                                                <input
                                                    type="checkbox"
                                                    checked={checkboxChecked}
                                                    disabled={isPurchasingAnim}
                                                    onChange={() => togglePurchased(itemKey)}
                                                    title={purchased || isPendingUnmark ? 'Desmarcar como comprado' : 'Marcar como comprado'}
                                                    data-test={`shopping-list-purchased-${itemKey}`}
                                                />
                                            )}
                                        </td>
                                        <td className={namePurchasedClass}>
                                            <div className="shopping-list-item-cell">
                                                <ShoppingListItemSelectAsync
                                                    ref={(el) => {
                                                        itemInputRefs.current[itemKey] = el;
                                                    }}
                                                    style={{width: '100%', minWidth: '100px', menuWidth: '180px'}}
                                                    textValue={item.catalogItem?.name ?? ''}
                                                    excludeNames={getFilledItemNames(itemKey)}
                                                    onItemClick={(name) => handleItemClick(name, itemKey)}
                                                    onInputValueChange={(inputValue) => handleItemInputChange(inputValue, itemKey)}
                                                    onBlur={() => commitItemNameOnBlur(itemKey)}
                                                    dataTest={`shopping-list-item-select-${itemKey}`}
                                                />
                                            </div>
                                        </td>
                                        <td>
                                            {filled && (
                                                <select
                                                    className="shopping-list-item-sector"
                                                    value={item.supermarketSector || ''}
                                                    onChange={(e) => handleItemChange(itemKey, 'supermarketSector', e.target.value)}
                                                    data-test={`shopping-list-sector-${itemKey}`}
                                                >
                                                    <option value="">Setor...</option>
                                                    {SUPERMARKET_SECTORS.map((sector) => (
                                                        <option key={sector} value={sector}>{sector}</option>
                                                    ))}
                                                </select>
                                            )}
                                        </td>
                                        <td>
                                            {filled && (
                                                <input
                                                    type="number"
                                                    className="shopping-list-item-qty"
                                                    value={parseQuantity(item.quantity)}
                                                    onChange={(e) => handleItemChange(itemKey, 'quantity', e.target.value)}
                                                    min="1"
                                                    data-test={`shopping-list-qty-${itemKey}`}
                                                />
                                            )}
                                        </td>
                                        <td className="shopping-list-item-actions-cell">
                                            {filled && (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="delivery-expand-btn shopping-list-expand-btn"
                                                        onClick={() => toggleObsRow(itemKey)}
                                                        title={obsExpanded ? 'Recolher observação' : 'Adicionar observação'}
                                                        data-test={`shopping-list-obs-toggle-${itemKey}`}
                                                    >
                                                        {obsExpanded ? '▲' : '▼'}
                                                    </button>
                                                    <DeleteButton
                                                        className="remove-button"
                                                        onClick={() => removeItem(itemKey)}
                                                        data-test={`remove-shopping-item-${itemKey}`}
                                                    />
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                    {filled && obsExpanded && (
                                        <tr className={`shopping-list-obs-expanded-row ${rowClass}`}>
                                            <td colSpan={5} className="shopping-list-obs-expanded-cell">
                                                <label className="shopping-list-obs-expanded-label">Observação</label>
                                                <input
                                                    type="text"
                                                    className="shopping-list-item-obs-expanded"
                                                    value={item.observation || ''}
                                                    onChange={(e) => handleItemChange(itemKey, 'observation', e.target.value)}
                                                    placeholder="Observação"
                                                    data-test={`shopping-list-obs-${itemKey}`}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ShoppingListManager;
